import CallEndRoundedIcon from '@mui/icons-material/CallEndRounded'
import PhoneInTalkRoundedIcon from '@mui/icons-material/PhoneInTalkRounded'
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import { useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import { getMentorPortalVoiceOptions } from 'src/mentor/services/portalVoiceCallService'
import {
  endPortalVoiceCall,
  extendPortalVoiceCall,
  getPortalVoiceCallState,
  startPortalVoiceCall,
  subscribePortalVoiceCallState,
} from './portalVoiceCallManager'

function formatRemainingTime(seconds) {
  const safeSeconds = Math.max(0, Number(seconds) || 0)
  const hours = Math.floor(safeSeconds / 3600)
  const minutes = Math.floor((safeSeconds % 3600) / 60)
  const secs = safeSeconds % 60

  if (hours > 0) {
    return `${hours}h ${minutes}m`
  }
  if (minutes > 0) {
    return `${minutes}m ${secs}s`
  }
  return `${secs}s`
}

function formatMinutesLabel(seconds) {
  const minutes = Math.max(1, Math.round((Number(seconds) || 0) / 60))
  return `${minutes} minute${minutes === 1 ? '' : 's'}`
}

export default function PortalVoiceCallButton({
  bookedEventId,
  uiSurface,
  renderTrigger,
  buttonLabel = 'Call',
  disabled = false,
  iconOnly = false,
  size = 'small',
  sx = {},
}) {
  const [anchorEl, setAnchorEl] = useState(null)
  const [loadingOptions, setLoadingOptions] = useState(false)
  const [loadingRoute, setLoadingRoute] = useState('')
  const [optionsState, setOptionsState] = useState({
    routes: [],
    error: '',
  })
  const [callState, setCallState] = useState(getPortalVoiceCallState())

  useEffect(() => subscribePortalVoiceCallState(setCallState), [])

  const isCallActive = callState?.status && callState.status !== 'idle'
  const isBusy = loadingOptions || !!loadingRoute || callState?.status === 'connecting'
  const isRenewing = !!callState?.renewalInFlight

  const triggerLabel = useMemo(() => {
    if (!isCallActive) {
      return buttonLabel
    }
    if (callState?.status === 'connecting') {
      return 'Connecting...'
    }
    if (callState?.remainingSeconds > 0) {
      return `Ends in ${formatRemainingTime(callState.remainingSeconds)}`
    }
    return 'Call Active'
  }, [buttonLabel, callState?.remainingSeconds, callState?.status, isCallActive])

  async function loadRoutes() {
    if (!bookedEventId) {
      return
    }
    setLoadingOptions(true)
    const result = await getMentorPortalVoiceOptions(bookedEventId)
    setOptionsState({
      routes: result?.routes || [],
      error: result?.ok ? '' : result?.message || 'Unable to load portal call routes.',
    })
    setLoadingOptions(false)
  }

  async function handleTriggerClick(event) {
    event?.preventDefault?.()
    event?.stopPropagation?.()

    if (disabled) {
      return
    }

    setAnchorEl(event?.currentTarget || null)
    if (!isCallActive) {
      await loadRoutes()
    }
  }

  async function handleRouteSelect(route) {
    setLoadingRoute(route?.destinationType || route?.id || '')
    setAnchorEl(null)
    try {
      await startPortalVoiceCall({
        bookedEventId,
        destinationType: route?.destinationType || route?.id,
        uiSurface,
      })
    } catch (error) {
      toast.error(error?.message || 'Unable to place the portal voice call.')
    } finally {
      setLoadingRoute('')
    }
  }

  async function handleExtendCall() {
    try {
      await extendPortalVoiceCall()
      setAnchorEl(null)
    } catch (error) {
      toast.error(error?.message || 'Unable to extend the portal voice call.')
    }
  }

  function handleEndCall() {
    setAnchorEl(null)
    endPortalVoiceCall()
  }

  const triggerProps = {
    onClick: handleTriggerClick,
    label: triggerLabel,
    busy: isBusy,
    active: isCallActive,
    disabled,
  }

  const triggerNode = renderTrigger ? (
    renderTrigger(triggerProps)
  ) : iconOnly ? (
    <Tooltip title={triggerLabel} arrow>
      <span>
        <IconButton
          size={size}
          onClick={handleTriggerClick}
          disabled={disabled}
          sx={{
            color: isCallActive ? '#dc2626' : '#2563eb',
            backgroundColor: isCallActive ? 'rgba(220, 38, 38, 0.12)' : 'rgba(37, 99, 235, 0.1)',
            border: `1px solid ${isCallActive ? 'rgba(220, 38, 38, 0.2)' : 'rgba(37, 99, 235, 0.2)'}`,
            ...sx,
          }}
        >
          {isBusy || isRenewing ? (
            <CircularProgress size={16} sx={{ color: 'inherit' }} />
          ) : isCallActive ? (
            <CallEndRoundedIcon fontSize="small" />
          ) : (
            <PhoneInTalkRoundedIcon fontSize="small" />
          )}
        </IconButton>
      </span>
    </Tooltip>
  ) : (
    <Button
      size={size}
      variant={isCallActive ? 'contained' : 'outlined'}
      color={isCallActive ? 'error' : 'primary'}
      onClick={handleTriggerClick}
      disabled={disabled}
      startIcon={
        isBusy || isRenewing ? (
          <CircularProgress size={14} color="inherit" />
        ) : isCallActive ? (
          <CallEndRoundedIcon />
        ) : (
          <PhoneInTalkRoundedIcon />
        )
      }
      sx={sx}
    >
      {triggerLabel}
    </Button>
  )

  return (
    <>
      {triggerNode}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        PaperProps={{
          sx: {
            minWidth: 300,
            borderRadius: '14px',
            boxShadow: '0 14px 34px rgba(15, 23, 42, 0.18)',
          },
        }}
      >
        {isCallActive ? (
          <Box sx={{ px: 2, py: 1.75, maxWidth: 360 }}>
            <Stack spacing={1.5}>
              <Stack spacing={0.5}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  Portal Call In Progress
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {[callState?.destinationLabel, callState?.maskedDestination]
                    .filter(Boolean)
                    .join(' • ') || 'Connected participant'}
                </Typography>
              </Stack>

              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Chip
                  size="small"
                  color={callState?.remainingSeconds > 0 ? 'warning' : 'default'}
                  label={
                    callState?.remainingSeconds > 0
                      ? `Ends in ${formatRemainingTime(callState.remainingSeconds)}`
                      : 'Ending soon'
                  }
                />
                {callState?.recordingEnabled ? (
                  <Chip size="small" color="info" variant="outlined" label="Recording enabled" />
                ) : null}
                {callState?.renewalEnabled ? (
                  <Chip
                    size="small"
                    color={callState?.renewalAllowed ? 'success' : 'default'}
                    variant="outlined"
                    label={
                      callState?.renewalAllowed
                        ? `Can extend by ${formatMinutesLabel(callState?.renewalIncrementSeconds)}`
                        : 'Max call duration reached'
                    }
                  />
                ) : null}
              </Stack>

              {callState?.error ? (
                <Typography variant="body2" color="error.main">
                  {callState.error}
                </Typography>
              ) : null}

              <Divider />

              <Stack direction="row" spacing={1.25} justifyContent="flex-end">
                {callState?.renewalEnabled ? (
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={handleExtendCall}
                    disabled={!callState?.renewalAllowed || isRenewing}
                  >
                    {isRenewing
                      ? 'Extending...'
                      : `Extend by ${formatMinutesLabel(callState?.renewalIncrementSeconds)}`}
                  </Button>
                ) : null}
                <Button size="small" color="error" variant="contained" onClick={handleEndCall}>
                  End Call
                </Button>
              </Stack>
            </Stack>
          </Box>
        ) : loadingOptions ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1.5 }}>
            <CircularProgress size={18} />
            <Typography variant="body2">Loading call routes...</Typography>
          </Box>
        ) : optionsState?.error ? (
          <Box sx={{ px: 2, py: 1.5, maxWidth: 320 }}>
            <Typography variant="body2" color="error.main">
              {optionsState.error}
            </Typography>
          </Box>
        ) : (
          (optionsState?.routes || []).map((route) => {
            const destinationType = route?.destinationType || route?.id
            const secondaryText = route?.enabled
              ? [route?.destinationLabel, route?.maskedDestination].filter(Boolean).join(' • ')
              : route?.disabledReason || 'This route is currently unavailable.'

            return (
              <MenuItem
                key={destinationType}
                onClick={() => route?.enabled && handleRouteSelect(route)}
                disabled={!route?.enabled || !!loadingRoute}
                sx={{
                  alignItems: 'flex-start',
                  py: 1.5,
                  whiteSpace: 'normal',
                }}
              >
                <ListItemText
                  primary={route?.title || route?.destinationLabel || destinationType}
                  secondary={secondaryText}
                  primaryTypographyProps={{ fontWeight: 600 }}
                  secondaryTypographyProps={{
                    sx: {
                      color: route?.enabled ? 'text.secondary' : 'warning.dark',
                      mt: 0.25,
                    },
                  }}
                />
              </MenuItem>
            )
          })
        )}
      </Menu>
    </>
  )
}
